import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Grid,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { parseFormattedMoneyToCents } from '../../helpers/formatters';
import { PLAN_QUOTAS, planQuotaPeriodLabel, UNLIMITED } from '../../helpers/planLimits';

function featuresToText(features) {
    return (Array.isArray(features) ? features : []).join('\n');
}

function textToFeatures(text) {
    return String(text || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
}

/** Campo vazio significa ilimitado, representado como -1 na API. */
function limitsToInputs(limits) {
    const out = {};
    PLAN_QUOTAS.forEach(({ key }) => {
        const value = limits?.[key];
        out[key] = value == null || Number(value) < 0 ? '' : String(Number(value));
    });
    return out;
}

function inputsToLimits(inputs) {
    const out = {};
    PLAN_QUOTAS.forEach(({ key }) => {
        const raw = String(inputs?.[key] ?? '').trim();
        out[key] = raw === '' ? UNLIMITED : Number(raw);
    });
    return out;
}

function invalidLimitKeys(inputs) {
    return PLAN_QUOTAS.filter(({ key }) => {
        const raw = String(inputs?.[key] ?? '').trim();
        if (raw === '') {
            return false;
        }
        const value = Number(raw);
        return !Number.isInteger(value) || value < 0;
    }).map(({ key }) => key);
}

function SubscriptionPlanForm({ isEdit }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checkingAccess, setCheckingAccess] = useState(!isMasterUser());
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [monthlyPrice, setMonthlyPrice] = useState('');
    const [featuresText, setFeaturesText] = useState('');
    const [limitInputs, setLimitInputs] = useState(() => limitsToInputs(null));
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setCheckingAccess(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isEdit || !isMaster || !id) {
            return undefined;
        }
        setIsLoading(true);
        api.get(`subscription-plans/${id}`)
            .then((response) => {
                const plan = response?.data?.data;
                if (!plan) {
                    setDidNotLoad(true);
                    return;
                }
                setName(plan.name || '');
                setDescription(plan.description || '');
                const cents = Number(plan.monthly_billed_value) || 0;
                setMonthlyPrice(cents > 0 ? (cents / 100).toFixed(2).replace('.', ',') : '0');
                setFeaturesText(featuresToText(plan.features));
                setLimitInputs(limitsToInputs(plan.limits));
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
        return undefined;
    }, [id, isEdit, isMaster]);

    const renderFieldError = (field) => {
        if (!resultErrors[field]) {
            return null;
        }
        return (
            <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {resultErrors[field]}
            </div>
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const nextErrors = { has_errors: false };
        const nameValue = name.trim();
        const descriptionValue = description.trim();
        const priceValue = parseFormattedMoneyToCents(null, monthlyPrice);

        if (!nameValue) {
            nextErrors.name = 'Informe o nome do plano.';
            nextErrors.has_errors = true;
        }
        if (Number.isNaN(priceValue) || priceValue < 0) {
            nextErrors.monthly_billed_value = 'Informe um valor mensal válido.';
            nextErrors.has_errors = true;
        }
        invalidLimitKeys(limitInputs).forEach((key) => {
            nextErrors[`limits.${key}`] = 'Informe um número inteiro igual ou maior que zero.';
            nextErrors.has_errors = true;
        });
        if (nextErrors.has_errors) {
            setResultErrors(nextErrors);
            return;
        }
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        const payload = {
            name: nameValue,
            description: descriptionValue,
            monthly_billed_value: priceValue,
            features: textToFeatures(featuresText),
            limits: inputsToLimits(limitInputs),
        };
        const request = isEdit
            ? api.put(`subscription-plans/${id}`, payload)
            : api.post('subscription-plans', payload);

        request
            .then(() => {
                navigate('/admin/subscription-plans');
            })
            .catch((error) => {
                const data = error.response?.data || {};
                const grpcMessage = String(error?.message || '').trim();
                if (data.errors && typeof data.errors === 'object') {
                    setResultErrors({ ...data.errors, has_errors: true });
                } else if (data.error) {
                    setResultErrors({ _form: data.error, has_errors: true });
                } else if (grpcMessage) {
                    setResultErrors({ _form: grpcMessage, has_errors: true });
                } else {
                    setResultErrors({ has_errors: true });
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (checkingAccess) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }
    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin/subscription-plans">
                    Planos de assinatura
                </Link>
                <Typography color="text.primary">
                    {isEdit ? 'Editando plano' : 'Adicionando plano'}
                </Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>
                {isEdit ? 'Editar plano de assinatura' : 'Adicionar plano de assinatura'}
            </Typography>
            {resultErrors._form ? (
                <Typography color="error" sx={{ mb: 2 }}>
                    {resultErrors._form}
                </Typography>
            ) : null}
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome"
                            fullWidth
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            helperText="Nome interno (ex.: Free, Growing, Pro)"
                        />
                        {renderFieldError('name')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Valor mensal (R$)"
                            fullWidth
                            value={monthlyPrice}
                            onChange={(event) => setMonthlyPrice(event.target.value)}
                            helperText="Use 0 para plano gratuito"
                        />
                        {renderFieldError('monthly_billed_value')}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Descrição"
                            fullWidth
                            multiline
                            minRows={2}
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                        />
                        {renderFieldError('description')}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Features"
                            fullWidth
                            multiline
                            minRows={8}
                            value={featuresText}
                            onChange={(event) => setFeaturesText(event.target.value)}
                            helperText="Uma feature por linha (exibidas na landing e no paywall)"
                        />
                        {renderFieldError('features')}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mt: 1 }}>
                            Limites de volume
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Deixe em branco para não limitar. Os limites mensais reiniciam no 1º dia de cada mês.
                        </Typography>
                        <Grid container spacing={2}>
                            {PLAN_QUOTAS.map((quota) => (
                                <Grid item xs={12} sm={6} md={4} key={quota.key}>
                                    <TextField
                                        label={quota.label}
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, step: 1 }}
                                        value={limitInputs[quota.key] ?? ''}
                                        onChange={(event) => setLimitInputs((current) => ({
                                            ...current,
                                            [quota.key]: event.target.value,
                                        }))}
                                        placeholder="Ilimitado"
                                        helperText={planQuotaPeriodLabel(quota.period)}
                                    />
                                    {renderFieldError(`limits.${quota.key}`)}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={() => navigate('/admin/subscription-plans')}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export function NewSubscriptionPlanPage() {
    return <SubscriptionPlanForm isEdit={false} />;
}

export function EditSubscriptionPlanPage() {
    return <SubscriptionPlanForm isEdit />;
}
